<script lang="ts">
  import clsx from 'clsx';
  import { getColorContext } from '@athena-lib/container-context.svelte';
  import { Text } from '@athena-lib/Text';
  import { objectEntries } from '@athena-lib/object-utils';
  import { type Responsive } from '@athena-lib/responsive-utils';
  import type { HTMLAttributes } from 'svelte/elements';
  import type { ListGroupOption } from './types';

  type ListGroupSelectableProps = {
    options: ListGroupOption[];
    disabled: boolean;
    className?: string;
    onChange: (option: ListGroupOption) => void;
    name: string;
  } & Omit<HTMLAttributes<HTMLUListElement>, 'class' | 'onchange' | 'disabled'>;

  const { options, disabled, className, onChange, name, ...restProps }: ListGroupSelectableProps =
    $props();

  const contextBackground = getColorContext();
  const colors = $derived.by(() => {
    const responsiveBackground: Responsive<string> = {
      lg:
        contextBackground.value.lg === 'dark'
          ? 'lg:bg-st-gray-800 lg:text-st-white'
          : 'lg:bg-st-white lg:text-st-black',
      md:
        contextBackground.value.md === 'dark'
          ? 'md:bg-st-gray-800 md:text-st-white'
          : 'md:bg-st-white md:text-st-black',
      sm:
        contextBackground.value.sm === 'dark'
          ? 'bg-st-gray-800 text-st-white'
          : 'bg-st-white text-st-black',
    };
    const responsiveSelected: Responsive<string> = {
      lg: contextBackground.value.lg === 'dark' ? 'lg:bg-st-gray-700' : 'lg:bg-st-gray-200',
      md: contextBackground.value.md === 'dark' ? 'md:bg-st-gray-700' : 'md:bg-st-gray-200',
      sm: contextBackground.value.sm === 'dark' ? 'bg-st-gray-700' : 'bg-st-gray-200',
    };
    const responsiveNotSelectedLabel: Responsive<string> = {
      lg: contextBackground.value.lg === 'dark' ? 'lg:text-gray-200' : 'lg:text-gray-600',
      md: contextBackground.value.md === 'dark' ? 'md:text-gray-200' : 'md:text-gray-600',
      sm: contextBackground.value.sm === 'dark' ? 'text-gray-200' : 'text-gray-600',
    };
    return {
      background: objectEntries(responsiveBackground)
        .map(([_, value]) => value)
        .join(' '),
      selected: objectEntries(responsiveSelected)
        .map(([_, value]) => value)
        .join(' '),
      notSelectedLabel: objectEntries(responsiveNotSelectedLabel)
        .map(([_, value]) => value)
        .join(' '),
    };
  });

  const uid = (Math.random() + 1).toString(36).substring(7) + '-';

  function autoselect(event: Event, selectedOption: ListGroupOption): void {
    if (selectedOption.selected || isDisabled(selectedOption)) {
      event.stopPropagation();
      return;
    }

    const option = { ...selectedOption, selected: !isSelected(selectedOption) };
    if (onChange) {
      onChange(option);
    }
  }

  function isSelected(option: ListGroupOption): boolean {
    return !!option.selected;
  }

  function isDisabled(option: ListGroupOption): boolean {
    return disabled || !!option.disabled;
  }
</script>

<ul
  {...restProps}
  class={clsx(
    className,
    colors.background,
    'w-full rounded-lg ring-blue-500 has-[:focus-visible]:ring-2',
  )}
  role="list"
>
  {#each options as option (option.value)}
    <li
      class:disabled={isDisabled(option)}
      data-active={isSelected(option)}
      class:text-st-gray-300={isDisabled(option)}
      class={clsx(
        isSelected(option) && !disabled ? colors.selected : undefined,
        'first:rounded-t-lg last:rounded-b-lg',
        'flex w-full truncate p-3',
        'relative',
      )}
    >
      <label
        class={clsx(
          !isSelected(option) && !disabled ? colors.notSelectedLabel : '',
          option.icon ? 'flex gap-3' : 'ms-3',
        )}
        class:w-full={option.formattedValue}
        class:font-bold={isSelected(option) && !disabled}
        for={uid + option.value}
      >
        {#if option.icon}
          <img src={option.icon} alt="" class="w-6 scale-150" />
        {/if}
        <input
          {name}
          type="radio"
          id={uid + option.value}
          onchange={(e) => autoselect(e, option)}
          value={option.value}
          checked={option.selected}
          class={clsx(
            disabled ? 'cursor-not-allowed' : 'cursor-pointer',
            'absolute inset-0 opacity-0',
          )}
        />
        <span class="grid grid-cols-3">
          {#if option.formattedValue}
            <Text class="text-nowrap text-start" color={disabled ? 'medium' : undefined}>
              {option.formattedValue}
            </Text>
          {/if}
          <Text
            class={clsx({ 'text-end': option.formattedValue }, 'text-nowrap text-start')}
            color={disabled ? 'medium' : undefined}
          >
            <!-- eslint-disable-next-line svelte/no-at-html-tags -->
            {@html option.title}
          </Text>
          {#if option.save && !disabled}
            <Text color="green" bold={true} class="sale-badge self-center text-right text-sm">
              - {option.save}%
            </Text>
          {/if}
        </span>
      </label>
    </li>
  {/each}
</ul>
